import { type FC } from "react";
import { DollarSign, TrendingUp, TrendingDown } from "lucide-react";

import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { formatDollars } from "@/shared/utils/formatters";

import { EstateBreakdown } from "./EstateBreakdown";

interface StatItemProps {
  icon: FC<{ size?: number; className?: string }>;
  label: string;
  value: string;
  valueClassName?: string;
}

function StatItem({ icon: Icon, label, value, valueClassName }: StatItemProps) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-primary bg-white px-5 py-4">
      <div className="flex items-center gap-1.5 text-secondary">
        <Icon size={14} />
        {label}
      </div>
      <span className={valueClassName ?? "text-lg font-semibold text-primary"}>{value}</span>
    </div>
  );
}

export const OverviewCard: FC = () => {
  const { deceasedUserId } = useDeceasedUserData();
  const { assets, debts, summary, isLoading } = useEstateItems(deceasedUserId);

  const netValue = summary?.netValue ?? 0;
  const totalAssets = summary?.totalAssets ?? 0;
  const totalDebts = summary?.totalDebts ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatItem
          icon={DollarSign}
          label="Net estate value"
          value={formatDollars({ amount: netValue, rounding: "whole" })}
        />
        <StatItem
          icon={TrendingUp}
          label="Total assets"
          value={formatDollars({ amount: totalAssets, rounding: "whole" })}
          valueClassName="text-lg font-semibold text-brand"
        />
        <StatItem
          icon={TrendingDown}
          label="Total debts"
          value={formatDollars({ amount: totalDebts, rounding: "whole" })}
          valueClassName="text-lg font-semibold text-error"
        />
      </div>
      {!isLoading && (
        <div className="rounded-lg border border-primary bg-white p-6">
          <EstateBreakdown assets={assets} debts={debts} netValue={netValue} />
        </div>
      )}
    </div>
  );
};
